import { c as _c } from "react/compiler-runtime";
import { IcoArrowRightSmall16 } from '@onefootprint/icons';
import { Button } from '@onefootprint/ui';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Children, createContext, isValidElement, type MouseEvent, type ReactNode, useContext } from 'react';
import useShowChatBeside from '../hooks/use-show-chat-beside';

// Only a walkthrough opts in to opening pages in place: elsewhere a dashboard link still opens a new
// tab, so the behaviour can be tried where it matters before it reaches every chat.
export const PageLinksInPlaceContext = createContext(false);

// A streamed reply wraps each word of the label in its own span, so the text has to be dug out of them.
const textOf = (node: ReactNode): string => Children.toArray(node).map(part => typeof part === 'string' || typeof part === 'number' ? String(part) : isValidElement<{
  children?: ReactNode;
}>(part) ? textOf(part.props.children) : '').join('');
type PageLinkProps = {
  href: string;
  /** The link is alone on its line, so it reads as the step's action rather than part of a sentence. */
  standalone?: boolean;
  children?: ReactNode;
};

// A Percy link to a dashboard page: on its own line a compact secondary button, in a sentence plain
// text. In a walkthrough it navigates in this tab and opens the widget, so the chat stays beside the page.
const PageLink = t0 => {
  const $ = _c(20);
  const {
    href,
    standalone,
    children
  } = t0;
  const inPlace = useContext(PageLinksInPlaceContext);
  const router = useRouter();
  const showChatBeside = useShowChatBeside();
  if (!standalone) {
    let t1;
    if ($[0] !== inPlace || $[1] !== showChatBeside) {
      t1 = inPlace ? {
        onClick: showChatBeside
      } : {
        target: "_blank",
        rel: "noopener noreferrer"
      };
      $[0] = inPlace;
      $[1] = showChatBeside;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== children || $[4] !== href || $[5] !== t1) {
      t2 = <Link href={href} className="underline" {...t1}>{children}</Link>;
      $[3] = children;
      $[4] = href;
      $[5] = t1;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  let t1;
  if ($[7] !== href || $[8] !== router || $[9] !== showChatBeside) {
    t1 = event => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
        return;
      }
      event.preventDefault();
      showChatBeside();
      router.push(href);
    };
    $[7] = href;
    $[8] = router;
    $[9] = showChatBeside;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const navigateInPlace = t1;
  let t2;
  if ($[11] !== inPlace || $[12] !== navigateInPlace) {
    t2 = inPlace ? {
      onClick: navigateInPlace
    } : {
      target: "_blank"
    };
    $[11] = inPlace;
    $[12] = navigateInPlace;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== children) {
    t3 = textOf(children);
    $[14] = children;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== href || $[17] !== t2 || $[18] !== t3) {
    t4 = <Button href={href} variant="secondary" size="compact" suffixIcon={IcoArrowRightSmall16} {...t2}>{t3}</Button>;
    $[16] = href;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  return t4;
};
export default PageLink;